import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Layers, LineChart, Sparkles, Compass, Target, Star,
  History, Play, Pause, DollarSign, TrendingUp, Repeat, ChartBar,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { G } from "@/components/GlossaryTerm";
import GlossarySidebar from "@/components/GlossarySidebar";
import DripCalculator from "@/components/DripCalculator";
import AnnotatedBacktest from "@/components/AnnotatedBacktest";
import usePageSeo from "@/hooks/usePageSeo";
import DownloadPill from "@/components/DownloadPill";

// Source of truth for the 45-second product tour. Chapters double as a
// clickable table-of-contents that seeks the video AND scrolls the page.
const VIDEO_SRC =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_dividend-builder-1/artifacts/3q26nz68_DPP_Ad_1_with_captions.mp4";

const CHAPTERS = [
  {
    id: "build",
    time: 0,
    icon: Layers,
    title: "Build a portfolio",
    body:
      "Add any US ticker or ETF with autocomplete, drag weight sliders, and toggle DRIP with one switch. Start from a curated dividend universe or import a CSV.",
    highlights: ["Ticker autocomplete", "Weight sliders", "DRIP toggle", "CSV import"],
  },
  {
    id: "backtest",
    time: 10,
    icon: LineChart,
    title: "Backtest with real data",
    body:
      "30+ years of yfinance price + dividend history power an instant backtest. Cached results, per-ticker contribution charts, and every metric that matters — YoC, CAGR, total return, monthly income.",
    highlights: ["Yield-on-cost", "CAGR", "Monthly income", "Cached results"],
  },
  {
    id: "income-target",
    time: 20,
    icon: Target,
    title: "AI Income Target Planner",
    body:
      "Tell DPP how much you want per month. Claude reverse-solves the exact lump-sum today, a smaller amount that grows into your goal, and the monthly DCA path — with a projection chart of income growth year by year.",
    highlights: ["Lump-sum", "DCA breakdown", "Growth chart", "Plain-English risks"],
    live: true,
  },
  {
    id: "research",
    time: 30,
    icon: Compass,
    title: "Research any ticker",
    body:
      "Company snapshot, price chart with 8 ranges, dividend safety score, payout ratio history, ex-dividend calendar, news, and an AI thesis — all one click from your builder.",
    highlights: ["Safety score", "Payout history", "Compare tickers", "AI thesis"],
  },
  {
    id: "track",
    time: 40,
    icon: Star,
    title: "Track, optimize, repeat",
    body:
      "Star tickers to your watchlist with live yield & price on the Dashboard. Snapshot every AI rebalance so you can compare 'before AI' vs 'after AI' months later.",
    highlights: ["Watchlist", "Optimizer history", "Snapshot compare", "PDF report"],
  },
];

export default function HowItWorks() {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(45);

  usePageSeo({
    title: "Investment 101 — DRIP, Yield-on-Cost & Backtesting Explained | DPP",
    description:
      "How DPP works, in plain English. 45-second product tour, interactive demo, and the ranking methodology we use for every ticker.",
    path: "/how-it-works",
    ogSlug: "how-it-works",
    jsonLdId: "how-it-works-video-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: "How Dividend Portfolio Planner Works — 45-second product tour",
      description:
        "Five chapter walkthrough: Build a portfolio, backtest against historical data, reverse-solve an income target with AI, research any ticker, and track your live holdings.",
      thumbnailUrl: ["https://dividendportfolioplanner.com/logo.png"],
      uploadDate: "2026-02-01",
      duration: "PT45S",
      contentUrl: VIDEO_SRC,
      publisher: { "@type": "Organization", name: "Dividend Portfolio Planner" },
    },
  });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setCurrentTime(v.currentTime);
    const onDur = () => setDuration(v.duration || 45);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onDur);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onDur);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, []);

  const jumpTo = useCallback((seconds, chapterId) => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = seconds;
      v.play().catch(() => {});
    }
    const el = document.getElementById(`chapter-${chapterId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const progress = duration ? Math.min(100, (currentTime / duration) * 100) : 0;
  const activeChapter =
    [...CHAPTERS].reverse().find((c) => currentTime >= c.time)?.id || CHAPTERS[0].id;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      {/* Header — glossary button now floats top-right and follows scroll */}
      <div className="max-w-2xl">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Investment 101</div>
        <h1 className="serif text-5xl sm:text-6xl mt-3">Investment 101.</h1>
        <p className="text-muted-foreground mt-4">
          Everything you need to plan a dividend portfolio like a pro — the concepts, the traps, and how DPP visualizes them. Skim in 8 minutes, revisit any term with the glossary. Prefer watching?{" "}
          <a
            href="https://www.youtube.com/@DivPortplan"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 hover:text-primary/80"
            data-testid="investment-101-youtube"
          >
            Click here to subscribe to our YouTube channel
          </a>
          {" "}for feature walkthroughs and quick explainers.
        </p>
      </div>

      {/* Floating glossary — fixed top-right, moves with scroll, green primary */}
      <GlossarySidebar variant="floating" />

      {/* Investment 101 educational sections — moved above the video tour */}
      <div className="mt-12 max-w-3xl mx-auto">
        <Investment101Nav />
        <Section1_WhatIsADividend />
        <Section2_YieldOnCost />
        <Section3_DRIP />
        <Section6_ReadingABacktest />
      </div>

      {/* 45-second video tour — supplementary walkthrough below the reading */}
      <div className="mt-8 mb-8">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">45-second product tour</div>
          <h2 className="serif text-4xl mt-2">Prefer to watch?</h2>
          <p className="text-muted-foreground mt-3 text-sm">
            The whole product end-to-end in three quarters of a minute. Click any chapter to jump into the video and scroll to details.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[7fr,5fr] gap-8 items-start">
        {/* Video card (sticky on desktop so it stays visible while user scrolls chapters) */}
        <div className="lg:sticky lg:top-6">
          <div className="relative">
            <div className="absolute -inset-4 sm:-inset-6 bg-primary/10 rounded-[2rem] rotate-[-0.5deg]" />
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-border bg-black">
              <video
                ref={videoRef}
                src={VIDEO_SRC}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onClick={togglePlay}
                className="w-full aspect-video object-cover cursor-pointer"
                data-testid="how-it-works-video"
              />
              {/* Progress rail with chapter markers */}
              <div className="absolute left-3 right-3 bottom-12 h-1 rounded-full bg-white/20">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-150"
                  style={{ width: `${progress}%` }}
                />
                {CHAPTERS.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => jumpTo(c.time, c.id)}
                    className="absolute -top-1.5 w-4 h-4 rounded-full bg-white border-2 border-primary hover:scale-125 transition-transform"
                    style={{ left: `calc(${(c.time / duration) * 100}% - 8px)` }}
                    title={`${c.title} · ${c.time}s`}
                    aria-label={`Jump to ${c.title}`}
                    data-testid={`marker-${c.id}`}
                  />
                ))}
              </div>
              {/* Play/Pause */}
              <div className="absolute bottom-3 right-3 flex gap-2">
                <button
                  type="button"
                  onClick={togglePlay}
                  className="rounded-full bg-black/60 hover:bg-black/80 text-white text-[11px] mono uppercase tracking-widest px-3 py-1.5 backdrop-blur-sm transition-colors inline-flex items-center gap-1.5"
                  data-testid="how-it-works-playpause"
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                  {playing ? "Pause" : "Play"}
                </button>
                <button
                  type="button"
                  onClick={() => { const v = videoRef.current; if (v) v.muted = !v.muted; }}
                  className="rounded-full bg-black/60 hover:bg-black/80 text-white text-[11px] mono uppercase tracking-widest px-3 py-1.5 backdrop-blur-sm transition-colors"
                  data-testid="how-it-works-mute"
                >
                  Toggle sound
                </button>
              </div>
              {/* Elapsed / total in the corner */}
              <div className="absolute top-3 left-3 mono text-[10px] uppercase tracking-widest text-white/80 bg-black/40 backdrop-blur-sm rounded-full px-2 py-0.5">
                {formatTime(currentTime)} / {formatTime(duration || 45)}
              </div>
            </div>
          </div>
        </div>

        {/* Chapter table of contents */}
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Chapters</div>
          <div className="space-y-2" data-testid="how-it-works-chapters">
            {CHAPTERS.map((c, i) => {
              const active = activeChapter === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => jumpTo(c.time, c.id)}
                  className={`w-full text-left rounded-lg border transition-colors px-4 py-3 flex items-start gap-3 ${
                    active
                      ? "border-primary bg-primary/[0.04]"
                      : "border-border bg-card hover:border-primary/40"
                  }`}
                  data-testid={`chapter-btn-${c.id}`}
                >
                  <div className={`shrink-0 w-9 h-9 rounded-md grid place-items-center ${
                    active ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
                  }`}>
                    <c.icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{formatTime(c.time)}</span>
                      <span className="serif text-base">{c.title}</span>
                      {c.live && <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/10 text-primary">Live</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.body}</div>
                  </div>
                  <div className="mono text-[10px] text-muted-foreground shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detailed chapter cards */}
      <div className="mt-16 space-y-16">
        {CHAPTERS.map((c, i) => (
          <div key={c.id} id={`chapter-${c.id}`} data-testid={`chapter-section-${c.id}`}>
            <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-6 items-start">
              <div className="shrink-0">
                <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center">
                  <c.icon className="w-6 h-6" />
                </div>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-3 text-center">
                  Chapter {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mono text-[10px] text-secondary mt-0.5 text-center">
                  {formatTime(c.time)}
                </div>
              </div>
              <Card className={c.live ? "border-primary/30" : ""}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="serif text-3xl">{c.title}</h2>
                    {c.live && (
                      <span className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                        Now live
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-foreground/85 leading-relaxed">{c.body}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {c.highlights.map((h) => (
                      <span
                        key={h}
                        className="mono text-[11px] uppercase tracking-widest px-2 py-1 rounded-full bg-muted text-foreground/80 border border-border"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                  <div className="mt-5">
                    <Button
                      variant="outline"
                      className="rounded-full"
                      onClick={() => jumpTo(c.time, c.id)}
                      data-testid={`replay-${c.id}`}
                    >
                      Replay this chapter
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <Card className="mt-20 overflow-hidden border-primary/30">
        <CardContent className="p-10 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-6 items-center">
          <div>
            <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Your turn</div>
            <h3 className="serif text-3xl mt-2">Build your first dividend portfolio in under a minute.</h3>
            <p className="text-muted-foreground mt-2">
              Free forever tier. No card required. Upgrade only if you outgrow the monthly AI cap.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={user ? "/portfolio/new" : "/auth"}>
              <Button size="lg" className="rounded-full px-6" data-testid="how-it-works-cta-start">
                Start planning <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="rounded-full px-6" data-testid="how-it-works-cta-pricing">
                See pricing
              </Button>
            </Link>
            <DownloadPill size="lg" testId="how-it-works-download" label="Download the app" />
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

function formatTime(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem.toString().padStart(2, "0")}`;
}

// ---------- Investment 101 sections ----------

function Investment101Nav() {
  const sections = [
    { id: "s1", label: "What a dividend is", icon: DollarSign },
    { id: "s2", label: "Yield-on-cost", icon: TrendingUp },
    { id: "s3", label: "The DRIP snowball", icon: Repeat },
    { id: "s6", label: "Reading a backtest", icon: ChartBar },
  ];
  return (
    <div className="mb-12">
      <div className="text-center mb-6">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Read on</div>
        <h2 className="serif text-4xl mt-2">Four concepts that decide your success.</h2>
        <p className="text-muted-foreground mt-3 text-sm">
          Every underlined word opens a plain-English definition. Tap the glossary bubble any time for the full 25-term reference.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {sections.map(({ id, label, icon: Icon }) => (
          <a
            key={id}
            href={`#${id}`}
            className="rounded-md border border-border bg-card hover:border-primary/40 transition-colors px-4 py-3 flex items-center gap-2"
            data-testid={`inv101-nav-${id}`}
          >
            <Icon className="w-4 h-4 text-secondary shrink-0" />
            <span className="text-sm">{label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function SectionShell({ id, kicker, title, icon: Icon, children }) {
  return (
    <section id={id} className="scroll-mt-20 mb-24" data-testid={`inv101-${id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4 text-secondary" />
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">{kicker}</div>
      </div>
      <h3 className="serif text-3xl sm:text-4xl leading-tight">{title}</h3>
      <div className="mt-6 space-y-5 text-[15px] leading-relaxed text-foreground/85">{children}</div>
    </section>
  );
}

function TryItCTA({ label = "Try it in DPP", to = "/portfolio/new", testId = "inv101-try" }) {
  return (
    <div className="mt-6 pt-4 border-t border-border/60 flex items-center justify-between flex-wrap gap-2">
      <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
        Now see it for yourself
      </div>
      <Link to={to}>
        <Button size="sm" className="rounded-full" data-testid={testId}>
          {label} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </Link>
    </div>
  );
}

// -------- Section 1: What is a dividend --------
function Section1_WhatIsADividend() {
  return (
    <SectionShell id="s1" kicker="Section 1 · The basics" title="What a dividend actually is" icon={DollarSign}>
      <p>
        A <G>dividend</G> is a cash payment a company sends to its shareholders — usually every quarter — out of the profits it earned that period. If you own 100 shares of a company that pays $1.00/year in dividends, that&apos;s $100 in cash landing in your brokerage account, split into four ~$25 payments.
      </p>
      <p>
        Not every company pays one. Growth-obsessed companies like Alphabet, Amazon, and Tesla reinvest every dollar back into the business. Mature companies — Coca-Cola, Procter &amp; Gamble, Chevron — pay a chunk of profits out to shareholders because they can&apos;t productively reinvest all of it. Those are the companies dividend investors hunt.
      </p>

      <div className="rounded-md border border-primary/25 bg-primary/[0.04] p-5">
        <div className="mono text-[10px] uppercase tracking-widest text-primary mb-2">A concrete example</div>
        <p className="text-sm">
          You invest <strong>$10,000</strong> in SCHD, an ETF with a ~3.5% <G>dividend yield</G>. In year one you&apos;ll receive about <strong>$350</strong>, paid quarterly (~$87/quarter). That&apos;s the income part.
        </p>
        <p className="text-sm mt-2">
          But SCHD has raised its dividend ~12%/year for the last decade. So by year 10, that same $10k position (without adding a dollar) is throwing off roughly <strong>$1,100/year</strong> — an <G>yield-on-cost</G> of ~11%. That&apos;s the growth part. Section 2 explains why this matters more than the current yield.
        </p>
      </div>

      <p>
        Two dates matter: the <G>ex-dividend date</G> (must own before this to get paid) and the payment date (when cash actually hits). If you buy on or after the ex-div date, you miss that quarter — the seller pockets it.
      </p>

      <p>
        <strong className="text-foreground">The one number that separates good dividends from bad ones:</strong> the <G>payout ratio</G>. Under 60% means the company keeps enough profit to keep growing. Over 100% means they&apos;re paying out more than they earn — usually right before a dividend cut. DPP&apos;s Ticker Research tab flags this automatically as part of its <G>dividend safety</G> score.
      </p>

      <TryItCTA label="Look up a ticker" to="/research" testId="inv101-try-research" />
    </SectionShell>
  );
}

// -------- Section 2: Yield vs Yield-on-Cost --------
function Section2_YieldOnCost() {
  return (
    <SectionShell id="s2" kicker="Section 2 · The DPP superpower" title="Yield vs Yield-on-Cost — why one grows and the other doesn't" icon={TrendingUp}>
      <p>
        <G>Dividend yield</G> is the number every finance site shows on a stock&apos;s page. It&apos;s just: <span className="mono">annual dividend ÷ current share price</span>. If a stock trades at $100 and pays $3/year, its yield is 3%.
      </p>
      <p>
        The problem: <strong>current yield tells you nothing about what you personally earn.</strong> It changes every second the price moves. The number that matters for a long-term investor is <G>yield-on-cost</G> — annual dividend ÷ <em>your</em> purchase price. Unlike current yield, YoC only moves in one direction: up (as long as the company keeps raising its dividend).
      </p>

      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/60">
            <tr className="text-left">
              <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Year</th>
              <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Share price</th>
              <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Annual dividend</th>
              <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Current yield</th>
              <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Your YoC</th>
            </tr>
          </thead>
          <tbody>
            {[
              { y: 0, p: 60, d: 1.80 },
              { y: 5, p: 92, d: 2.75 },
              { y: 10, p: 141, d: 4.20 },
              { y: 15, p: 216, d: 6.42 },
              { y: 20, p: 331, d: 9.81 },
            ].map((r) => (
              <tr key={r.y} className={`border-t border-border ${r.y === 0 || r.y === 20 ? "bg-secondary/[0.04]" : ""}`}>
                <td className="px-3 py-2 mono">{r.y}</td>
                <td className="px-3 py-2 mono text-right">${r.p}</td>
                <td className="px-3 py-2 mono text-right">${r.d.toFixed(2)}</td>
                <td className="px-3 py-2 mono text-right text-muted-foreground">{((r.d / r.p) * 100).toFixed(2)}%</td>
                <td className="px-3 py-2 mono text-right text-primary font-semibold">{((r.d / 60) * 100).toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        Look at year 20. The stock&apos;s current yield is still ~3% — barely moved. But <strong className="text-primary">your yield-on-cost is 16.4%</strong>. Every dollar you invested 20 years ago is now spitting out 16.4¢ of income every year, and it&apos;ll keep climbing. That&apos;s the number retirees live off.
      </p>
      <p>
        This is why DPP shows YoC as a first-class metric everywhere — on the backtest results, on Track My Holdings, and inside the AI Optimizer. Current yield is a snapshot; YoC is the trajectory.
      </p>

      <div className="rounded-md border border-secondary/30 bg-secondary/[0.05] p-5">
        <div className="mono text-[10px] uppercase tracking-widest text-secondary mb-2">The trade-off</div>
        <p className="text-sm">
          High current yield often means low dividend growth (or a <G>yield trap</G>). Low current yield with high growth means your YoC will crush the high-yielder&apos;s in 10–15 years. This is the core choice every dividend portfolio makes.
        </p>
      </div>

      <TryItCTA label="Backtest a portfolio" testId="inv101-try-yoc" />
    </SectionShell>
  );
}

// -------- Section 3: DRIP snowball --------
function Section3_DRIP() {
  return (
    <SectionShell id="s3" kicker="Section 3 · Compounding, visualized" title="DRIP — how a $10k position becomes $47k" icon={Repeat}>
      <p>
        <G>DRIP</G> stands for Dividend Reinvestment Plan. Every dividend you receive is automatically used to buy more shares of the same stock — no commission, no button to press. Your quarterly $87 dividend becomes fractional shares that themselves start paying dividends next quarter. That&apos;s where the <G>snowball effect</G> comes from.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-md border border-destructive/30 bg-destructive/[0.03] p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-destructive mb-1">DRIP OFF</div>
          <div className="serif text-3xl">$28,270</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            $10,000 in SCHD, 20 years, dividends paid to cash and spent. You still earn ~$1,050/year in year 20 — but nothing compounded.
          </p>
        </div>
        <div className="rounded-md border border-primary/40 bg-primary/[0.05] p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-primary mb-1">DRIP ON</div>
          <div className="serif text-3xl text-primary">$47,930</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Same $10,000, same 20 years, same stock — but every dividend bought more shares. Year-20 income: <strong className="text-primary">$4,180/year</strong>.
          </p>
        </div>
      </div>

      <p>
        Same investment. Same stock. Same market. The only difference is whether the quarterly dividend gets spent or reinvested. Over 20 years the compounding version is worth <strong>~70% more</strong> — and the income it produces is four times larger.
      </p>

      <p>
        This is why the DRIP toggle at the top of every DPP portfolio is not a small detail. Turn it on, run a 20-year <G>backtest</G>, and watch the two lines diverge. Then imagine 30 years.
      </p>

      {/* Interactive calculator so section 3 becomes hands-on, not just illustrative */}
      <DripCalculator />

      <div className="rounded-md border border-border bg-muted/30 p-5">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">The math, if you like math</div>
        <p className="text-sm">
          A stock paying a 3% yield growing 8%/year, with DRIP on: your effective annual <G>CAGR</G> from dividends alone is roughly <strong>3% × (1 + 8%) = 3.24%</strong> compounding on top of price appreciation. Over 30 years, that&apos;s the difference between doubling your money once and doubling it three times.
        </p>
      </div>

      <TryItCTA label="Toggle DRIP in a backtest" testId="inv101-try-drip" />
    </SectionShell>
  );
}

// -------- Section 6: Reading a backtest --------
function Section6_ReadingABacktest() {
  return (
    <SectionShell id="s6" kicker="Section 6 · Using DPP" title="How to read a backtest without fooling yourself" icon={ChartBar}>
      <p>
        A <G>backtest</G> replays a portfolio&apos;s rules on real historical data — what would $10k invested in SCHD + VYM + JEPI on Jan 1, 2010 be worth today? DPP&apos;s engine crunches thousands of daily price + dividend records to answer that in under a second.
      </p>
      <p>
        But a backtest is <strong>not a promise</strong>. It&apos;s a stress-test. Here&apos;s what to actually look at:
      </p>

      {/* Real-shape annotated chart — four numbers pinned to the events on the curve */}
      <AnnotatedBacktest />

      <p className="mt-2">
        DPP shows all four numbers side-by-side on every backtest, plus a comparison mode where you can pin two portfolios and see the winners row-by-row. The AI Optimizer&apos;s explanation card walks you through <em>why</em> one beat the other, in plain English.
      </p>

      <div className="rounded-md border border-secondary/30 bg-secondary/[0.05] p-5">
        <div className="mono text-[10px] uppercase tracking-widest text-secondary mb-2">The honest disclaimer</div>
        <p className="text-sm">
          Past performance doesn&apos;t guarantee future returns — no backtest can capture regime changes, tax law shifts, or your own emotional response to a -40% year. Use DPP&apos;s backtests to <em>compare</em> ideas and rule out bad ones, not to predict the future.
        </p>
      </div>

      <TryItCTA label="Run your first backtest" testId="inv101-try-backtest" />
    </SectionShell>
  );
}

